/** Floor-mode display helpers — weight bands, ticket lines, Challenge 25 dates. */

/** BR-PRC-022 example picking tolerance when the pick-list omits a band. */
export const DEFAULT_WEIGHT_TOLERANCE_BP = 1000;

/** BR-PRC-021 / OQ-716 working authorisation buffer when the quote omits it. */
export const DEFAULT_AUTH_BUFFER_BP = 1500;
export const DEFAULT_AUTH_BUFFER_MIN_PENCE = 100;

/** 07-11 floor `motion.settle` — nothing may move under a descending finger. */
export const FLOOR_TOUCH_SETTLE_MS = 3000;

export const ACCEPTED_IDENTIFICATION = [
  'UK photocard driving licence',
  'Passport',
  'PASS-accredited card',
  'Biometric residence permit',
] as const;

export const AGE_CHECK_ID_TYPES = [
  'PHOTOCARD_DRIVING_LICENCE',
  'PASSPORT',
  'PASS_CARD',
  'BIOMETRIC_RESIDENCE_PERMIT',
] as const;

export const AGE_REFUSE_REASONS = [
  'APPEARS_UNDER_AGE',
  'NO_IDENTIFICATION',
  'ID_FAILED',
  'OTHER',
] as const;

export const SHORT_PICK_REASONS = [
  'OUT_OF_STOCK',
  'DAMAGED',
  'EXPIRED',
  'QUALITY_UNACCEPTABLE',
  'NOT_FOUND',
  'WRONG_LOCATION',
] as const;

export type TicketLine = {
  name: string;
  quantity: number;
  modifiers: string[];
  allergens: string[];
  allergenRemoved: string[];
};

export function parseItemSummaryLines(summary?: string | null): TicketLine[] {
  if (!summary?.trim()) return [];
  return summary
    .split(/[,;]|\n/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = part.match(/^(\d+)\s*[x×]\s*(.+)$/i);
      if (match) {
        return {
          name: match[2].trim(),
          quantity: Number(match[1]),
          modifiers: [],
          allergens: [],
          allergenRemoved: [],
        };
      }
      return {
        name: part,
        quantity: 1,
        modifiers: [],
        allergens: [],
        allergenRemoved: [],
      };
    });
}

export function ticketLinesFromOrder(order: {
  lines?: Array<{
    name?: string;
    productName?: string;
    quantity?: number | null;
    modifiers?: Array<{ name?: string; priceDelta?: number | null } | string>;
    allergens?: string[] | null;
    allergenRemoved?: string[] | null;
    notes?: string | null;
  }>;
  itemSummary?: string | null;
  linesSummary?: string | null;
}): TicketLine[] {
  if (order.lines?.length) {
    return order.lines.map((line) => ({
      name: line.name ?? line.productName ?? 'Item',
      quantity: line.quantity ?? 1,
      modifiers: (line.modifiers ?? [])
        .map((mod) => (typeof mod === 'string' ? mod : (mod.name ?? '')))
        .filter(Boolean),
      allergens: line.allergens ?? [],
      allergenRemoved: line.allergenRemoved ?? [],
    }));
  }
  return parseItemSummaryLines(order.itemSummary ?? order.linesSummary);
}

export function isBlockedKitchenOrder(order: {
  hasVariableWeightItems?: boolean | null;
  needsAttentionReason?: string | null;
  blocked?: boolean | null;
  weightBlocked?: boolean | null;
}): boolean {
  if (order.blocked || order.weightBlocked) return true;
  const reason = (order.needsAttentionReason ?? '').toUpperCase();
  return reason.includes('AMENDMENT') || reason.includes('WEIGHT') || reason.includes('VARIABLE');
}

export function sortKitchenTickets<T extends { promisedAt?: string | null; status?: string }>(
  orders: T[],
): T[] {
  return [...orders].sort((a, b) => {
    const aLate = isOverdue(a.promisedAt);
    const bLate = isOverdue(b.promisedAt);
    if (aLate !== bLate) return aLate ? -1 : 1;
    const aT = a.promisedAt ? Date.parse(a.promisedAt) : Number.POSITIVE_INFINITY;
    const bT = b.promisedAt ? Date.parse(b.promisedAt) : Number.POSITIVE_INFINITY;
    return aT - bT;
  });
}

/** Keep ticket card positions stable for `FLOOR_TOUCH_SETTLE_MS` after a touch. */
export function stabilizeKitchenTicketOrder<T extends { id?: string | null }>(
  previous: T[],
  next: T[],
): T[] {
  if (previous.length === 0) return next;
  const nextById = new Map(next.filter((item) => item.id).map((item) => [item.id as string, item]));
  const kept: T[] = [];
  const seen = new Set<string>();
  for (const item of previous) {
    if (!item.id) continue;
    const fresh = nextById.get(item.id);
    if (!fresh) continue;
    kept.push(fresh);
    seen.add(item.id);
  }
  for (const item of next) {
    if (item.id && seen.has(item.id)) continue;
    kept.push(item);
  }
  return kept;
}

export function authorisationBufferMinor(
  estimatedMeasurePricedMinor: number,
  bufferBp = DEFAULT_AUTH_BUFFER_BP,
  minPence = DEFAULT_AUTH_BUFFER_MIN_PENCE,
): number {
  const fromBp = Math.round((Math.max(0, estimatedMeasurePricedMinor) * bufferBp) / 10000);
  return Math.max(minPence, fromBp);
}

export function isDeltaWithinAuthBuffer(
  deltaMinor: number,
  estimatedMeasurePricedMinor: number,
): boolean {
  if (estimatedMeasurePricedMinor <= 0) return Math.abs(deltaMinor) === 0;
  return Math.abs(deltaMinor) <= authorisationBufferMinor(estimatedMeasurePricedMinor);
}

export function isOverdue(promisedAt?: string | null): boolean {
  if (!promisedAt) return false;
  const t = Date.parse(promisedAt);
  return Number.isFinite(t) && t < Date.now();
}

export function countdownLabel(promisedAt?: string | null, now = Date.now()): string {
  if (!promisedAt) return 'No due time';
  const ms = Date.parse(promisedAt) - now;
  if (!Number.isFinite(ms)) return 'No due time';
  const mins = Math.round(ms / 60000);
  if (mins < 0) return `${Math.abs(mins)}m overdue`;
  if (mins === 0) return 'Due now';
  return `${mins}m left`;
}

/** 07-05 KDS time fact: LATE once (`LATE 6m`), never `LATE 6m overdue`. */
export function ticketTimeFact(promisedAt?: string | null, now = Date.now()): string {
  if (!promisedAt) return 'No due time';
  const ms = Date.parse(promisedAt) - now;
  if (!Number.isFinite(ms)) return 'No due time';
  const mins = Math.round(ms / 60000);
  if (mins < 0) return `LATE ${Math.abs(mins)}m`;
  if (mins === 0) return 'Due now';
  return `${mins}m left`;
}

export function orderLabel(order: {
  publicReference?: string | null;
  reference?: string | null;
  id?: string;
}): string {
  const ref = order.publicReference ?? order.reference;
  if (ref) {
    const digits = ref.replace(/\D/g, '');
    if (digits.length >= 3) return `#${digits.slice(-4)}`;
    return ref;
  }
  const id = order.id ?? '';
  return id ? `#${id.slice(-4).toUpperCase()}` : 'Order';
}

export function weightAcceptRange(
  requiredGrams: number,
  toleranceBp = DEFAULT_WEIGHT_TOLERANCE_BP,
): { min: number; max: number } {
  const delta = Math.round((requiredGrams * toleranceBp) / 10000);
  return { min: Math.max(1, requiredGrams - delta), max: requiredGrams + delta };
}

export function weightVariance(
  actualGrams: number,
  requiredGrams: number,
): { direction: 'over' | 'under' | 'on'; grams: number } {
  const grams = actualGrams - requiredGrams;
  if (grams > 0) return { direction: 'over', grams };
  if (grams < 0) return { direction: 'under', grams: Math.abs(grams) };
  return { direction: 'on', grams: 0 };
}

export function isWeightOutOfTolerance(actualGrams: number, min: number, max: number): boolean {
  return actualGrams < min || actualGrams > max;
}

/** 07-05 wf48 — a reading above 3× ordered is almost always a unit error. */
export function isTripleOrderedWeight(actualGrams: number, requiredGrams: number): boolean {
  return requiredGrams > 0 && actualGrams > requiredGrams * 3;
}

/** Ticket ids that arrived since the last paint — used for the new/late kitchen chime. */
export function newKitchenTicketIds(
  previousIds: readonly string[],
  nextIds: readonly string[],
): string[] {
  const seen = new Set(previousIds);
  return nextIds.filter((id) => id && !seen.has(id));
}

/** Line total in minor units from grams and £/kg (or any /kg) minor price. */
export function lineTotalMinorFromWeight(weightGrams: number, unitPricePerKgMinor: number): number {
  return Math.round((weightGrams / 1000) * unitPricePerKgMinor);
}

export function formatGbpFromMinor(minor: number): string {
  return `£${(minor / 100).toFixed(2)}`;
}

export function formatPencePerKg(minor: number): string {
  return `${formatGbpFromMinor(minor)} / kg`;
}

/**
 * Latest date of birth that meets `minimumAge` today.
 * Prefer the server Challenge 25 payload (`qualifyingDateOfBirthLabel`); this is the offline fallback.
 */
export function qualifyingDateOfBirth(minimumAge = 18, at = new Date()): string {
  const d = new Date(at.getFullYear() - minimumAge, at.getMonth(), at.getDate());
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function unresolvedWeightCount(
  items: Array<{
    requiresWeight?: boolean | null;
    requiredWeightGrams?: number | null;
    pickedWeightGrams?: number | null;
    weightGrams?: number | null;
  }>,
): number {
  return items.filter((item) => isVariableWeightItem(item) && !hasRecordedWeight(item)).length;
}

export function isVariableWeightItem(item: {
  requiresWeight?: boolean | null;
  requiredWeightGrams?: number | null;
}): boolean {
  return (
    item.requiresWeight === true ||
    (item.requiredWeightGrams != null && item.requiredWeightGrams > 0)
  );
}

export function hasRecordedWeight(item: {
  pickedWeightGrams?: number | null;
  weightGrams?: number | null;
}): boolean {
  const grams = item.pickedWeightGrams ?? item.weightGrams;
  return grams != null && Number(grams) > 0;
}

export function nextPendingPickItem<T extends { status?: string | null; id?: string }>(
  items: T[],
  currentId?: string | null,
): T | undefined {
  const pending = items.filter((item) => {
    const status = (item.status ?? 'PENDING').toUpperCase();
    return status === 'PENDING' || status === 'PARTIAL';
  });
  if (!currentId) return pending[0];
  const idx = pending.findIndex((item) => item.id === currentId);
  return pending[idx + 1] ?? pending[0];
}
